/**
 * Conversation List Presenter Component
 * Presentational component for displaying a list of conversations
 */

import React from 'react';
import { ConversationListItem } from './ConversationListItem';
import { ChatConversation } from '@/store/chatScreenStore';
import { ChatLocation, ChatMessage } from '@/types/chatTypes';
import { spacing, backgroundColors, strokeColors } from '@/design-system/utils';

export interface ConversationListPresenterProps {
  conversations: ChatConversation[];
  onPlaceSelect?: (location: ChatLocation, conversationId: string) => void;
  onCardClick?: (cardId: string, conversationId: string) => void;
  onPlaceNameClick?: (placeName: string, conversationId: string) => void;
}

export const ConversationListPresenter: React.FC<
  ConversationListPresenterProps
> = ({ conversations, onPlaceSelect, onCardClick, onPlaceNameClick }) => {
  const handlePlaceSelect = (
    location: ChatLocation,
    conversationId: string
  ) => {
    onPlaceSelect?.(location, conversationId);
  };

  const handleCardClick = (cardId: string, conversationId: string) => {
    onCardClick?.(cardId, conversationId);
  };

  const handlePlaceNameClick = (placeName: string, conversationId: string) => {
    onPlaceNameClick?.(placeName, conversationId);
  };

  // Flatten conversations into message items
  // Each assistant message (with its preceding user message) becomes a separate conversation item
  const messageItems = React.useMemo(() => {
    console.log('📋 [ConversationListPresenter] Processing conversations:', {
      totalConversations: conversations.length,
      conversationIds: conversations.map(c => c.id),
      conversationSessionIds: conversations.map(c => c.sessionId),
    });

    const items: Array<{
      id: string;
      conversationId: string;
      sessionId: string;
      messages: ChatMessage[];
      title: string;
      conversationIndex: number;
      messageIndex: number;
    }> = [];

    conversations.forEach((conversation, convIndex) => {
      console.log(
        `📋 [ConversationListPresenter] Processing conversation ${convIndex + 1}:`,
        {
          conversationId: conversation.id,
          sessionId: conversation.sessionId,
          totalMessages: conversation.messages.length,
          messageRoles: conversation.messages.map(m => m.role),
          messageSessionIds: conversation.messages.map(m => m.session_id),
          hasExperiences: conversation.messages.some(
            m => m.experiences && m.experiences.length > 0
          ),
          experienceCounts: conversation.messages
            .filter(m => m.experiences)
            .map(m => ({ role: m.role, count: m.experiences?.length || 0 })),
        }
      );

      // Group messages into pairs (user + assistant)
      const messagePairs: ChatMessage[][] = [];
      let currentPair: ChatMessage[] = [];

      conversation.messages.forEach(message => {
        if (message.role === 'user') {
          // Start a new pair
          if (currentPair.length > 0) {
            messagePairs.push([...currentPair]);
          }
          currentPair = [message];
        } else if (message.role === 'assistant') {
          // Add assistant message to current pair
          currentPair.push(message);
          // Complete the pair and add it
          messagePairs.push([...currentPair]);
          currentPair = [];
        }
      });

      // Add any remaining messages
      if (currentPair.length > 0) {
        messagePairs.push([...currentPair]);
      }

      console.log(
        `📋 [ConversationListPresenter] Created ${messagePairs.length} message pairs for conversation ${conversation.id}`
      );

      // Create a conversation item for each message pair
      messagePairs.forEach((pair, pairIndex) => {
        const userMessage = pair.find(m => m.role === 'user');
        const assistantMessage = pair.find(m => m.role === 'assistant');
        const title =
          userMessage?.content?.substring(0, 50) || conversation.title;

        const itemId = `${conversation.id}_msg_${pairIndex}`;
        const item = {
          id: itemId,
          conversationId: conversation.id,
          sessionId: conversation.sessionId,
          messages: pair, // Only the messages for this specific response
          title,
          conversationIndex: convIndex,
          messageIndex: pairIndex,
        };

        console.log(`📋 [ConversationListPresenter] Created message item:`, {
          itemId,
          conversationId: conversation.id,
          sessionId: conversation.sessionId,
          messageCount: pair.length,
          messageRoles: pair.map(m => m.role),
          messageSessionIds: pair.map(m => m.session_id),
          hasExperiences:
            assistantMessage?.experiences &&
            assistantMessage.experiences.length > 0,
          experienceCount: assistantMessage?.experiences?.length || 0,
          experienceNames:
            assistantMessage?.experiences?.map(e => e.name) || [],
        });

        items.push(item);
      });
    });

    console.log('📋 [ConversationListPresenter] Total message items created:', {
      totalItems: items.length,
      itemIds: items.map(i => i.id),
      itemSessionIds: items.map(i => i.sessionId),
    });

    return items;
  }, [conversations]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[6],
        width: '100%',
        paddingBottom: spacing[4],
      }}
    >
      {messageItems.map((item, index) => (
        <div
          key={item.id}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Message Item Header */}
          <div
            style={{
              padding: spacing[3],
              backgroundColor: backgroundColors.secondary,
              borderBottom: `1px solid ${strokeColors.base}`,
              marginBottom: spacing[2],
            }}
          >
            <h3
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
                margin: 0,
              }}
            >
              {item.title}
            </h3>
            {item.conversationIndex > 0 || item.messageIndex > 0 ? (
              <p
                style={{
                  fontSize: '12px',
                  color: '#666',
                  margin: '4px 0 0 0',
                }}
              >
                Conversation {item.conversationIndex + 1}, Response{' '}
                {item.messageIndex + 1}
              </p>
            ) : null}
          </div>

          {/* Message Item Content - Each message gets its own tabs and response */}
          <ConversationListItem
            conversationId={item.id}
            sessionId={item.sessionId}
            initialMessages={item.messages}
            onPlaceSelect={location =>
              handlePlaceSelect(location, item.conversationId)
            }
            onCardClick={cardId => handleCardClick(cardId, item.conversationId)}
            onPlaceNameClick={placeName =>
              handlePlaceNameClick(placeName, item.conversationId)
            }
          />

          {/* Divider between message items (except last) */}
          {index < messageItems.length - 1 && (
            <div
              style={{
                width: '100%',
                height: '2px',
                backgroundColor: strokeColors.base,
                marginTop: spacing[6],
                marginBottom: spacing[6],
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
};
